import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import ChatBubble from './ChatBubble';
import FinishWithHumanPill from './FinishWithHumanPill';
import '../../styles/onboarding.css';
import HgSmiley from './SmileyIcon';
import SendArrowIcon from './SendArrowIcon';

/**
 * OnboardingChatPanel
 * Right-panel chat interface. Wired to the onboarding chat API.
 */
const OnboardingChatPanel = ({
  chatHistory,
  onSendMessage,
  isLoading,
  preloadedQuestions,
  header,
  inputPlaceholder,
  subtitle,
  avatarEmoji,
}) => {
  const cmsContent = useSelector((s) => s.Onboarding?.cmsContent || {});
  const cms = (key, fallback = '') => cmsContent[key] || fallback;
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const prevLenRef = useRef(chatHistory.length);
  const isMountRef = useRef(true);
  const textareaRef = useRef(null);

  const resizeTextarea = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = parseInt(getComputedStyle(el).lineHeight, 10) || 20;
    const maxHeight = lineHeight * 2 + 16; // 2 lines + padding
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  };

  useEffect(() => {
    const prev = prevLenRef.current;
    prevLenRef.current = chatHistory.length;

    if (isMountRef.current) {
      // On mount / back-navigation: jump instantly so there is no scroll animation flicker.
      isMountRef.current = false;
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    } else if (chatHistory.length > prev) {
      // A genuinely new message arrived — smooth scroll.
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory]);

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (onSendMessage) onSendMessage(trimmed);
    setInputValue('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChipClick = (label) => {
    if (onSendMessage) onSendMessage(label);
  };

  const titleNode = header !== null && header !== undefined ? header : cms('onboarding_chat_panel_title', 'Your set-up guide');
  const subtitleText = subtitle || cms('onboarding_chat_panel_subtitle', 'Right here the whole way · replies instantly');
const placeholderText = inputPlaceholder || cms('onboarding_chat_panel_input_placeholder', 'Ask me anything...');

  return (
    <div className="hg-chat-panel" data-testid="onboarding-chat-panel">
      <div className="hg-chat-header">
        <div className="hg-chat-header__main">
          <div className="hg-chat-header__avatar" aria-hidden="true">
            <HgSmiley />
          </div>
          <div className="hg-chat-header__text">
            <div className="hg-chat-header__title-row">
              <span className="hg-chat-header__title">{titleNode}</span>
              <span className="hg-chat-header__status-dot" aria-hidden="true" />
            </div>
            {subtitleText && (
              <div className="hg-chat-header__subtitle" dangerouslySetInnerHTML={{ __html: subtitleText }} />
            )}
          </div>
        </div>
        <div className="hg-chat-header__footer">
          <FinishWithHumanPill variant="inline" />
        </div>
      </div>

      {/* Message area */}
      <div className="hg-chat-messages hg-ob-scroll">
        {chatHistory.map((msg, idx) => {
          if (!msg.message?.trim()) return null;
          return (
            <ChatBubble key={idx} role={msg.role}>
              {msg.message}
            </ChatBubble>
          );
        })}

        {isLoading && (
          <ChatBubble role="bot">
            <span className="hg-typing-indicator">
              <span /><span /><span />
            </span>
          </ChatBubble>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className='hg-chat__divider' />

      {/* Input row */}
      <div className="hg-chat-input-row">
        <textarea
          ref={textareaRef}
          className="hg-chat-input"
          rows={1}
          placeholder={placeholderText}
          value={inputValue}
          onChange={(e) => { setInputValue(e.target.value); resizeTextarea(e.target); }}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className="hg-chat-send-btn"
          onClick={handleSend}
          aria-label="Send message"
        >
         <SendArrowIcon />
        </button>
      </div>
    </div>
  );
};

OnboardingChatPanel.propTypes = {
  chatHistory:        PropTypes.array,
  onSendMessage:      PropTypes.func,
  isLoading:          PropTypes.bool,
  preloadedQuestions: PropTypes.arrayOf(PropTypes.string),
  header:             PropTypes.node,
  inputPlaceholder:   PropTypes.string,
  subtitle:           PropTypes.string,
  avatarEmoji:        PropTypes.string,
};

OnboardingChatPanel.defaultProps = {
  chatHistory:        [],
  onSendMessage:      () => {},
  isLoading:          false,
  preloadedQuestions: [],
  header:             null,
  avatarEmoji:        '\u{1F60A}',
};

export default OnboardingChatPanel;
